import React, { memo, useCallback, useMemo } from 'react'

import { Switch } from '@mui/material'
import { FieldInputProps, useField } from 'formik'
import { useDeepCompareMemo } from 'use-deep-compare'

import { FieldSettings, FormHelpersProviderReturn } from '../../../ana-toolbox'
import { getHasError } from '../../utils/helpers/getHasError'
import { useFormHelpers } from '../../utils/hooks/useFormHelpers'
import { useSubmitCount } from '../../utils/hooks/useSubmitCount'
import { ToggleContainer } from '../../utils/shared/ToggleContainer'
import { ToggleFormControlLabel } from '../../utils/shared/ToggleFormControlLabel'
import { combinedFields } from '../../utils/yup/main'
import { FieldHelperText } from '../field-helper-text/field-helper-text'
import { Translate } from '../translate/translate'

export type ToggleFieldProps = {
  className?: string
  label: React.ReactNode
  name: string
  helperText?: React.ReactNode
  disabled?: boolean
}

const ToggleFieldBase = memo(
  ({
    className,
    label,
    error,
    helperText,
    ...props
  }: ToggleFieldProps & FieldInputProps<unknown> & { error?: string }) => {
    return (
      <>
        <ToggleContainer className={className}>
          <ToggleFormControlLabel
            label={<Translate>{label}</Translate>}
            error={Boolean(error)}
            labelPlacement="start"
            control={<Switch {...props} checked={Boolean(props.value)} />}
          />
        </ToggleContainer>

        <FieldHelperText name={props.name} helperText={helperText} error={error} />
      </>
    )
  }
)

ToggleFieldBase.displayName = 'ToggleFieldBase'

export const ToggleField = (props: ToggleFieldProps) => {
  const [field, meta] = useField(props.name)
  const submitCount = useSubmitCount()
  const formHelpers = useFormHelpers()

  const hasError = useMemo(() => getHasError(meta, submitCount ?? 1), [meta, submitCount])

  const memoizedField = useDeepCompareMemo(() => field, [field])

  const fieldSettings = useMemo(
    () => combinedFields[props.name as keyof typeof combinedFields] as FieldSettings,
    [props.name]
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      fieldSettings?.hooks?.beforeChange?.(e.target.checked, formHelpers as FormHelpersProviderReturn, e)
      field.onChange({ target: { value: e.target.checked, name: e.target.name } })
    },
    // Ignoring these dependencies to prevent unnecessary re renderes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <ToggleFieldBase error={hasError ? meta.error : undefined} {...memoizedField} {...props} onChange={handleChange} />
  )
}
